import { Descriptions, Table, TableColumnsType } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  cancelEventAttendance,
  cancelEventRegister,
  createEventAttendance,
  getEventInfo,
  getEventRegister,
} from '../../../api/events.api';
import Button from '../../../components/Button';
import Checkbox from '../../../components/Checkbox';
import DownloadButton from '../../../components/DownloadButton';
import ErrorResponse from '../../../components/ErrorResponse';
import SelectParentModal from '../../../components/event/briefing/register/modals/SelectParentModal';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import config from '../../../lib/config';
import exportTable from '../../../lib/exportTable';
import { getCommonCodeLabel } from '../../../lib/store';
import {
  IEventRegister,
  IGetEventInfo,
  IGetEventRegister,
} from '../../../models/events.model';
import { doLogout } from '../../../store/auth.slice';

interface IMatch {
  eventNo: string;
}

const BriefingRegister = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const dispatch = useDispatch();
  const match = useRouteMatch<IMatch>();
  const eventNo = Number(match.params.eventNo);
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetEventInfo>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetEventRegister>();
  // for excel download
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetEventRegister>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError,
    execute: submitExecute } = useAxios();
  const { data: cancelRes, isLoading: cancelIsLoading, error: cancelError,
    execute: cancelExecute } = useAxios();

  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});
  const [requireName, setRequireName] = useState('');
  const [webId, setWebId] = useState('');

  const [selectParentVisible, setSelectParentVisible] = useState(false);
  const [isCancelSelectAll, setIsCancelSelectAll] = useState(false);
  const [cancelSelects, setCancelSelects] = useState<number[]>([]);

  useEffect(() => {
    doGetEventRegister();
  }, [location]);

  useEffect(() => {
    if (cancelRes && !cancelIsLoading) {
      doGetEventRegister();
      successNotification(t('common.message.cancel'));
    }
  }, [cancelRes, cancelIsLoading]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      doGetEventRegister();
      successNotification(t('common.message.save1'));
    }
  }, [submitRes, submitIsLoading]);

  useEffect(() => {
    if (res3?.data && !isLoading3) {
      const data: any = _.cloneDeep(res3.data);
      data.forEach((item: any) => {
        item.attendStatus = getCommonCodeLabel('BR0008', item.attendStatus, 'ko');
        item.regDate = moment(item.regDate).format('YYYY.MM.DD HH:mm');
        item.attendSaveDate = item.attendSaveDate ? moment(item.attendSaveDate).format('YYYY.MM.DD HH:mm') : '';
      });
      const newColumns = _.cloneDeep(columns).filter((_: any, i: number) => i !== 4);
      const currentDate = moment(new Date());
      exportTable(newColumns, data, `${t('event.briefing.1')}_(${currentDate.format('YYYYMMDD')})`, t('event.briefing.1'), [12, 10, 20, 10, 20, 20]);
    }
  }, [res3, isLoading3]);

  const doGetEventRegister = () => {
    execute(getEventInfo('PA', eventNo));

    const _param: any = {
      pageNo: page,
      perPage: config.pageSize,
    };

    if (query.page) {
      const _page = Number(query.page);
      setPage(_page);
      _param.pageNo = _page;
    }
    if (query.requireName) {
      const _requireName = String(query.requireName);
      setRequireName(_requireName);
      _param.requireName = _requireName;
    }
    if (query.webId) {
      const _webId = String(query.webId);
      setWebId(_webId);
      _param.webId = _webId;
    }

    execute2(getEventRegister('PA', eventNo, _param));
    _param.page = _param.pageNo;
    delete _param.pageNo;
    delete _param.perPage;
    setParam(_param);
  };

  const onSetCancelSelects = (eventRequireCode: number) => {
    if (cancelSelects.includes(eventRequireCode)) {
      setCancelSelects(
        cancelSelects.filter((item) => item !== eventRequireCode),
      );
      setIsCancelSelectAll(false);
    } else {
      setCancelSelects([...cancelSelects, eventRequireCode]);
    }
  };

  const onSetCancelSelectAll = () => {
    if (isCancelSelectAll) {
      setCancelSelects([]);
      setIsCancelSelectAll(false);
    } else {
      const _selects: number[] = [];
      res2?.data?.forEach((item) => {
        if (item.cancelYn === 'N' && !item.attendStatus) {
          _selects.push(item.eventRequireCode);
        }
      });
      setCancelSelects(_selects);
      setIsCancelSelectAll(true);
    }
  };

  const onSearch = () => {
    const param: any = { page: 1 };

    if (requireName) param.requireName = requireName;
    if (webId) param.webId = webId;

    history.push(
      `/event/briefing/${eventNo}/register?${QueryString.stringify(param)}`,
    );
  };

  const onCancel = () => {
    if (!cancelSelects.length) {
      return infoNotification(t('event.education.message10'));
    }

    const req: any = [];
    cancelSelects.forEach((item) => {
      req.push({ eventRequireCode: item });
    });

    cancelExecute(cancelEventRegister(eventNo, req));
  };

  const onSubmit = (eventRequireCode: number) => {
    if (!res?.data?.eventDate || !res?.data?.eventTime) return;

    const timeLimit = moment(
      `${res.data.eventDate}T${res.data.eventTime.replace(':', '')}`,
    ).add(10, 'minutes');
    submitExecute(
      createEventAttendance(eventRequireCode, {
        attendStatus: moment().isAfter(timeLimit) ? 'LT' : 'AT',
      }),
    );
  };

  const onCancelAttendance = (eventRequireCode: number) => {
    cancelExecute(cancelEventAttendance(eventRequireCode));
  };

  const onExportTable = () => {
    execute3(getEventRegister('PA', eventNo, {
      ...param,
      pageNo: 1,
      perPage: 999999999,
    }));
  };

  const columns: TableColumnsType<IEventRegister> = [
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusKorName',
      align: 'center',
    },
    {
      title: t('event.education.column22'),
      dataIndex: 'memKorName',
      align: 'center',
    },
    {
      title: 'ID',
      dataIndex: 'webId',
      align: 'center',
    },
    {
      title: t('event.education.column23'),
      dataIndex: 'attendStatus',
      align: 'center',
      render: (value, record) => {
        return value
          ? (
            <>
              {getCommonCodeLabel('BR0008', value, 'ko')}
              {value !== 'AB' && (
                <Button
                  onClick={() => onCancelAttendance(record.eventRequireCode)}
                  className="btn btn-sm btn-secondary btn-w40p ml-1"
                  text={t('common.button.cancel')}
                />
              )}
            </>
            )
          : record.cancelYn === 'Y'
            ? t('common.button.cancel')
            : (
              <Button
                onClick={() => onSubmit(record.eventRequireCode)}
                className="btn btn-sm btn-success btn-w70p"
                text={t('event.education.button6')}
              />
              )
      },
    },
    {
      title: (
        <>
          {t('event.education.column26')}{' '}
          <Checkbox
            checked={isCancelSelectAll}
            onChange={() => {
              onSetCancelSelectAll();
            }}
            text=""
          />
        </>
      ),
      dataIndex: 'eventRequireCode',
      align: 'center',
      render: (value, record) => {
        let checked = false;
        if ((cancelSelects.includes(value) || isCancelSelectAll) && record.cancelYn === 'N' && !record.attendStatus) {
          checked = true;
        }

        return (
          <Checkbox
            checked={checked}
            onChange={() => onSetCancelSelects(value)}
            text=""
            disabled={record.cancelYn === 'Y' || !!record.attendStatus}
          />
        );
      },
    },
    {
      title: t('event.education.column27'),
      dataIndex: 'regDate',
      align: 'center',
      render: (value) => moment(value).format('YYYY.MM.DD HH:mm'),
    },
    {
      title: t('event.education.column28'),
      dataIndex: 'attendSaveDate',
      align: 'center',
      render: (value) =>
        value ? moment(value).format('YYYY.MM.DD HH:mm') : '',
    },
  ];

  return (
    <div className="content scrollbar">
      {(isLoading || isLoading2 || isLoading3 || submitIsLoading || cancelIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={submitError} />
      <ErrorResponse error={cancelError} />
      <SimpleHeader
        iconFile="icon-event-big.svg"
        parentMenu={t('common.menu.event')}
        currentMenu={t('event.briefing.title5')}
      />

      <div className='comm-table__area rowType'>
        <Descriptions
          bordered
          column={3}
          size="small"
          labelStyle={{ width: '16.6%' }}
          contentStyle={{ width: '16.6%' }}
        >
          <Descriptions.Item label={t('event.education.column10')} span={2}>
            {res?.data?.eventName}
          </Descriptions.Item>
          <Descriptions.Item label={t('event.briefing.column1')}>
            {getCommonCodeLabel('BR0017', res?.data?.eventCategory || '', 'ko')}
          </Descriptions.Item>
          <Descriptions.Item label={t('event.education.column29')}>
            {res?.data?.requireCount}
          </Descriptions.Item>
          <Descriptions.Item label={t('event.education.column30')}>
            {res?.data?.paymentCount}
          </Descriptions.Item>
          <Descriptions.Item label={t('event.education.column31')}>
            {res?.data?.attendanceRate}
          </Descriptions.Item>
          <Descriptions.Item label={t('event.education.column32')}>
            {res?.data?.normal}
          </Descriptions.Item>
          <Descriptions.Item label={t('event.education.column33')}>
            {res?.data?.late}
          </Descriptions.Item>
          <Descriptions.Item label={t('event.education.column34')}>
            {res?.data?.absent}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="flex flex-justify-end mt-4 mb-2">
        <input
          type="text"
          className="form-control w-150p mr-2"
          placeholder={t('event.education.column22')}
          value={requireName}
          onChange={(e) => setRequireName(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') onSearch();
          }}
        />
        <input
          type="text"
          className="form-control w-150p mr-2"
          placeholder="ID"
          value={webId}
          onChange={(e) => setWebId(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') onSearch();
          }}
        />
        <div className="comm-btn__area">
          <Button
            className="btn-basic small black"
            onClick={onSearch}
            text={t('common.button.search')}
          />
        </div>
      </div>
      <div className="comm-table__area mt-0">
        <Table
          columns={columns}
          dataSource={res2?.data}
          pagination={{
            current: page,
            pageSize: config.pageSize,
          }}
          rowKey="eventRequireCode"
        />
      </div>
      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <DownloadButton onClick={onExportTable} />
          <Button
            onClick={() => history.push('/event/briefing')}
            className="btn-basic gray-line"
            text={t('common.button.list')}
          />
          <Button
            onClick={() => {
              history.push(`/event/attendance?eventNo=${eventNo}&type=PA`);
              dispatch(doLogout());
            }}
            className="btn-basic pink-line"
            text={t('event.education.button7')}
          />            
        </div>
        <div className="common-paging center">
          <Pagination
            count={res2?.total || 0}
            page={page}
            param={param}
            path={`/event/briefing/${eventNo}/register`}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            onClick={() => setSelectParentVisible(true)}
            className="btn-basic pink-line"
            text={t('common.button.add')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            onClick={onCancel}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>
      
      {selectParentVisible &&
        <SelectParentModal
          visible={selectParentVisible}
          setVisible={setSelectParentVisible}
          eventNo={eventNo}
          doGetEventRegister={doGetEventRegister}
        />
      }
    </div>
  );
};

export default BriefingRegister;
